// localStorage存储管理工具
import { ElMessage } from 'element-plus'

/**
 * localStorage存储管理类
 * 用于管理应急明细数据的缓存和清理
 */
class StorageManager {
  constructor() {
    this.EMERGENCY_DETAILS_KEY = 'emergencyDetails'
    this.STORAGE_STATS_KEY = 'storageStats'
    this.MAX_CACHE_SIZE = 50 * 1024 * 1024 // 50MB最大缓存
    this.MAX_CACHE_AGE = 7 * 24 * 60 * 60 * 1000 // 7天最大缓存时间
    this.CLEANUP_THRESHOLD = 0.8 // 80%阈值触发清理
  }

  /**
   * 获取localStorage使用统计
   */
  getStorageStats() {
    try {
      const stats = {
        total: 0,
        emergencyDetails: 0,
        savedEvents: 0,
        other: 0,
        items: {}
      }

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        const value = localStorage.getItem(key) || ''
        const size = new Blob([value]).size

        stats.total += size
        stats.items[key] = {
          size: size,
          sizeKB: Math.round(size / 1024 * 100) / 100,
          lastModified: this.getItemTimestamp(key)
        }

        // 分类统计
        if (key === this.EMERGENCY_DETAILS_KEY) {
          stats.emergencyDetails += size
        } else if (key === 'savedEvents') {
          stats.savedEvents += size
        } else {
          stats.other += size
        }
      }

      return {
        ...stats,
        totalKB: Math.round(stats.total / 1024 * 100) / 100,
        totalMB: Math.round(stats.total / (1024 * 1024) * 100) / 100,
        emergencyDetailsKB: Math.round(stats.emergencyDetails / 1024 * 100) / 100,
        emergencyDetailsMB: Math.round(stats.emergencyDetails / (1024 * 1024) * 100) / 100,
        usagePercent: Math.round((stats.total / this.MAX_CACHE_SIZE) * 100)
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('获取存储统计失败:', error)
      }
      return {
        total: 0,
        totalKB: 0,
        totalMB: 0,
        emergencyDetails: 0,
        emergencyDetailsKB: 0,
        emergencyDetailsMB: 0,
        usagePercent: 0,
        items: {}
      }
    }
  }

  /**
   * 获取项目时间戳
   */
  getItemTimestamp(key) {
    try {
      const statsKey = `${key}_timestamp`
      const timestamp = localStorage.getItem(statsKey)
      return timestamp ? new Date(timestamp) : new Date()
    } catch (error) {
      return new Date()
    }
  }

  /**
   * 设置项目时间戳
   */
  setItemTimestamp(key) {
    try {
      const statsKey = `${key}_timestamp`
      localStorage.setItem(statsKey, new Date().toISOString())
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('设置时间戳失败:', error)
      }
    }
  }

  /**
   * 清理过期的emergencyDetails缓存
   */
  cleanupExpiredCache() {
    try {
      const emergencyDetails = JSON.parse(localStorage.getItem(this.EMERGENCY_DETAILS_KEY) || '{}')
      const now = Date.now()
      let cleanedCount = 0
      let totalCount = Object.keys(emergencyDetails).length

      // 清理过期数据
      Object.keys(emergencyDetails).forEach(key => {
        const item = emergencyDetails[key]
        const timestamp = item._cacheTimestamp || 0
        
        if (now - timestamp > this.MAX_CACHE_AGE) {
          delete emergencyDetails[key]
          cleanedCount++
        }
      })

      // 更新localStorage
      if (cleanedCount > 0) {
        localStorage.setItem(this.EMERGENCY_DETAILS_KEY, JSON.stringify(emergencyDetails))
        this.setItemTimestamp(this.EMERGENCY_DETAILS_KEY)
        if (process.env.NODE_ENV === 'development') {
          console.log(`清理了 ${cleanedCount}/${totalCount} 个过期缓存项`)
        }
      }

      return { cleanedCount, totalCount, remainingCount: totalCount - cleanedCount }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('清理过期缓存失败:', error)
      }
      return { cleanedCount: 0, totalCount: 0, remainingCount: 0 }
    }
  }

  /**
   * 按使用频率清理emergencyDetails缓存
   */
  cleanupByUsage() {
    try {
      const emergencyDetails = JSON.parse(localStorage.getItem(this.EMERGENCY_DETAILS_KEY) || '{}')
      const entries = Object.entries(emergencyDetails)
      
      if (entries.length === 0) return { cleanedCount: 0, totalCount: 0 }

      // 按访问时间和使用次数排序
      entries.sort((a, b) => {
        const aAccess = a[1]._lastAccess || 0
        const bAccess = b[1]._lastAccess || 0
        const aUsage = a[1]._usageCount || 0
        const bUsage = b[1]._usageCount || 0
        
        // 优先按使用次数，再按访问时间
        if (aUsage !== bUsage) return aUsage - bUsage
        return aAccess - bAccess
      })

      // 保留最常用的一半
      const keepCount = Math.ceil(entries.length / 2)
      const newDetails = {}
      
      for (let i = entries.length - keepCount; i < entries.length; i++) {
        const [key, value] = entries[i]
        newDetails[key] = value
      }

      localStorage.setItem(this.EMERGENCY_DETAILS_KEY, JSON.stringify(newDetails))
      this.setItemTimestamp(this.EMERGENCY_DETAILS_KEY)

      const cleanedCount = entries.length - keepCount
      if (process.env.NODE_ENV === 'development') {
        console.log(`按使用频率清理了 ${cleanedCount}/${entries.length} 个缓存项`)
      }

      return { cleanedCount, totalCount: entries.length, remainingCount: keepCount }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('按使用频率清理缓存失败:', error)
      }
      return { cleanedCount: 0, totalCount: 0, remainingCount: 0 }
    }
  }

  /**
   * 完全清空emergencyDetails缓存
   */
  clearEmergencyDetailsCache() {
    try {
      const stats = this.getStorageStats()
      const itemCount = Object.keys(JSON.parse(localStorage.getItem(this.EMERGENCY_DETAILS_KEY) || '{}')).length
      
      localStorage.removeItem(this.EMERGENCY_DETAILS_KEY)
      localStorage.removeItem(`${this.EMERGENCY_DETAILS_KEY}_timestamp`)
      
      // ElMessage.success(`已清空应急明细缓存：${itemCount}个项目，释放${stats.emergencyDetailsMB}MB空间`)
      return { clearedItems: itemCount, freedMB: stats.emergencyDetailsMB }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('清空缓存失败:', error)
      }
      ElMessage.error('清空缓存失败')
      return { clearedItems: 0, freedMB: 0 }
    }
  }

  /**
   * 自动清理：根据存储使用情况决定清理策略
   */
  autoCleanup() {
    try {
      const stats = this.getStorageStats()
      
      // 如果使用率超过阈值，进行清理
      if (stats.usagePercent >= (this.CLEANUP_THRESHOLD * 100)) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`存储使用率${stats.usagePercent}%，触发自动清理`)
        }
        
        // 首先清理过期数据
        const expiredResult = this.cleanupExpiredCache()
        
        // 如果清理过期数据后仍然超过阈值，按使用频率清理
        const newStats = this.getStorageStats()
        if (newStats.usagePercent >= (this.CLEANUP_THRESHOLD * 100)) {
          const usageResult = this.cleanupByUsage()
          return {
            type: 'full',
            expired: expiredResult,
            usage: usageResult,
            finalStats: this.getStorageStats()
          }
        }
        
        return {
          type: 'expired_only',
          expired: expiredResult,
          finalStats: this.getStorageStats()
        }
      }
      
      return {
        type: 'no_cleanup_needed',
        stats: stats
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('自动清理失败:', error)
      }
      return {
        type: 'error',
        error: error.message
      }
    }
  }

  /**
   * 缓存明细数据（替代直接localStorage操作）
   */
  cacheDetailData(key, data) {
    try {
      const emergencyDetails = JSON.parse(localStorage.getItem(this.EMERGENCY_DETAILS_KEY) || '{}')
      
      // 添加缓存元数据
      emergencyDetails[key] = {
        ...data,
        _cacheTimestamp: Date.now(),
        _lastAccess: Date.now(),
        _usageCount: (emergencyDetails[key]?._usageCount || 0) + 1
      }
      
      localStorage.setItem(this.EMERGENCY_DETAILS_KEY, JSON.stringify(emergencyDetails))
      this.setItemTimestamp(this.EMERGENCY_DETAILS_KEY)
      
      // 自动清理检查
      setTimeout(() => this.autoCleanup(), 100)
      
      return true
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('缓存明细数据失败:', error)
      }
      return false
    }
  }

  /**
   * 获取缓存的明细数据
   */
  getCachedDetailData(key) {
    try {
      const emergencyDetails = JSON.parse(localStorage.getItem(this.EMERGENCY_DETAILS_KEY) || '{}')
      const data = emergencyDetails[key]
      
      if (data) {
        // 更新访问时间和使用次数
        data._lastAccess = Date.now()
        data._usageCount = (data._usageCount || 0) + 1
        
        // 检查是否过期
        const age = Date.now() - (data._cacheTimestamp || 0)
        if (age > this.MAX_CACHE_AGE) {
          delete emergencyDetails[key]
          localStorage.setItem(this.EMERGENCY_DETAILS_KEY, JSON.stringify(emergencyDetails))
          return null
        }
        
        // 更新缓存
        emergencyDetails[key] = data
        localStorage.setItem(this.EMERGENCY_DETAILS_KEY, JSON.stringify(emergencyDetails))
        
        return data
      }
      
      return null
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('获取缓存明细数据失败:', error)
      }
      return null
    }
  }

  /**
   * 初始化存储管理（应用启动时调用）
   */
  initialize() {
    try {
      // 应用启动时进行一次自动清理
      const result = this.autoCleanup()
      if (process.env.NODE_ENV === 'development') {
        // console.log('存储管理器初始化完成:', result)
      }

      // 设置定期清理（每小时检查一次）
      this._cleanupTimer = setInterval(() => {
        this.autoCleanup()
      }, 60 * 60 * 1000)

      return result
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('存储管理器初始化失败:', error)
      }
      return { type: 'error', error: error.message }
    }
  }

  /**
   * 销毁定时器，防止内存泄漏（页面卸载时调用）
   */
  dispose() {
    if (this._cleanupTimer) {
      clearInterval(this._cleanupTimer)
      this._cleanupTimer = null
    }
  }
}

// 创建单例实例
const storageManager = new StorageManager()

export default storageManager